// Blog data and utility functions - now using API

// Cache for blog posts (client-side only)
let blogPostsCache = null;

// Fetch blog posts from API
async function fetchBlogPosts() {
  try {
    const response = await fetch("/api/blog", {
      cache: "no-store", // Always fetch fresh data
    });
    if (!response.ok) {
      throw new Error("Failed to fetch blog posts");
    }
    const posts = await response.json();
    blogPostsCache = posts;
    return posts;
  } catch (error) {
    console.error("Error fetching blog posts:", error);
    // Return empty array on error
    return [];
  }
}

// Get blog posts (async for both client and server)
export async function getBlogPosts() {
  if (typeof window === "undefined") {
    // Server-side: read directly from file system
    try {
      const fs = await import("fs");
      const path = await import("path");
      const dataFilePath = path.join(process.cwd(), "src/data/blog-posts.json");
      
      if (fs.existsSync(dataFilePath)) {
        const fileData = fs.readFileSync(dataFilePath, "utf8");
        const posts = JSON.parse(fileData);
        return posts.sort(
          (a, b) => new Date(b.publishedAt) - new Date(a.publishedAt)
        );
      }
      // Return empty array if file doesn't exist yet
      return [];
    } catch (error) {
      console.error("Error reading blog posts on server:", error);
      return [];
    }
  }

  // Client-side: use cache or fetch
  if (blogPostsCache) {
    return blogPostsCache.sort(
      (a, b) => new Date(b.publishedAt) - new Date(a.publishedAt)
    );
  }
  
  // Fetch if not cached
  const posts = await fetchBlogPosts();
  return posts.sort(
    (a, b) => new Date(b.publishedAt) - new Date(a.publishedAt)
  );
}

export async function getFeaturedPosts() {
  const posts = await getBlogPosts();
  return posts.filter((post) => post.featured);
}

export async function getPostBySlug(slug) {
  const posts = await getBlogPosts();
  return posts.find((post) => post.slug === slug);
}

export async function getPostsByCategory(category) {
  const posts = await getBlogPosts();
  return posts.filter((post) => post.category === category);
}

export async function getPostsByTag(tag) {
  const posts = await getBlogPosts();
  return posts.filter((post) => post.tags.includes(tag));
}

export async function getAllCategories() {
  const posts = await getBlogPosts();
  const categories = [...new Set(posts.map((post) => post.category))];
  return categories;
}

export async function getAllTags() {
  const posts = await getBlogPosts();
  const tags = [...new Set(posts.flatMap((post) => post.tags))];
  return tags;
}

// Save a new blog post
export async function saveBlogPost(postData) {
  try {
    const response = await fetch("/api/blog", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(postData),
    });

    if (!response.ok) {
      throw new Error("Failed to save blog post");
    }

    const newPost = await response.json();
    
    // Clear cache to force refresh
    blogPostsCache = null;
    
    // Dispatch custom event to notify other components
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("blogPostsUpdated"));
    }
    
    return newPost;
  } catch (error) {
    console.error("Error saving blog post:", error);
    throw error;
  }
}

// Delete a blog post
export async function deleteBlogPost(postId) {
  try {
    const response = await fetch(`/api/blog?id=${postId}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error("Failed to delete blog post");
    }

    // Clear cache to force refresh
    blogPostsCache = null;
    
    // Dispatch custom event to notify other components
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("blogPostsUpdated"));
    }
    
    return true;
  } catch (error) {
    console.error("Error deleting blog post:", error);
    throw error;
  }
}
